import React from 'react';
import { ArrowRight, ArrowLeft, Check, Bot, MessageSquare, Phone, Cpu, Workflow, Zap, Target, Users, Building2, Briefcase, TrendingUp, Settings, Shield, Link2, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CookieBanner from '../components/layout/CookieBanner';

const whyAI = [
  'Respond to leads instantly',
  'Automate repetitive tasks',
  'Improve customer experience',
  'Reduce operational costs',
  'Scale without adding headcount'
];

const aiSolutions = [
  {
    icon: MessageSquare,
    title: 'Custom AI Chatbots',
    description: 'Intelligent chatbots trained on your business, services, and FAQs to engage visitors, qualify leads, and book appointments 24/7.',
    useCases: ['Website chat & lead capture', 'Customer support', 'Appointment scheduling', 'Product & service education']
  },
  {
    icon: Phone,
    title: 'AI Voice Solutions',
    description: 'Human-like AI voice agents that answer calls, qualify leads, follow up, and route conversations automatically.',
    useCases: ['Inbound call handling', 'Lead qualification', 'Missed call follow-ups', 'Customer service & FAQs']
  },
  {
    icon: Bot,
    title: 'AI Agents',
    description: 'Autonomous AI agents that think, decide, and take action across tools and platforms.',
    useCases: ['Lead nurturing & follow-ups', 'CRM updates & data management', 'Sales & support workflows', 'Internal task execution']
  },
  {
    icon: Workflow,
    title: 'AI Automations',
    description: 'End-to-end AI-powered workflows that eliminate manual work and improve efficiency.',
    useCases: ['CRM & pipeline automation', 'Email, SMS & messaging workflows', 'Lead routing & scoring', 'Reporting & data syncing']
  }
];

const ourApproach = [
  { icon: Settings, text: 'Custom-built for your business' },
  { icon: Shield, text: 'Secure & scalable architecture' },
  { icon: Link2, text: 'Integrated with your existing tools' },
  { icon: BarChart3, text: 'Designed for ROI, not hype' }
];

const process = [
  {
    step: '01',
    title: 'AI Strategy & Discovery',
    description: 'We identify where AI can create the biggest impact in your business.'
  },
  {
    step: '02',
    title: 'Solution Design',
    description: 'We design custom AI systems tailored to your workflows and objectives.'
  },
  {
    step: '03',
    title: 'Build & Integration',
    description: 'We build, train, and integrate AI with your existing platforms.'
  },
  {
    step: '04',
    title: 'Testing & Optimization',
    description: 'We test performance, refine outputs, and optimize for accuracy and efficiency.'
  },
  {
    step: '05',
    title: 'Launch & Ongoing Support',
    description: 'Your AI goes live with monitoring, updates, and continuous improvement.'
  }
];

const whoIsFor = [
  { icon: TrendingUp, title: 'Businesses looking to scale efficiently' },
  { icon: Target, title: 'Sales & marketing teams' },
  { icon: Users, title: 'Customer support & service companies' },
  { icon: Briefcase, title: 'High-volume lead businesses' },
  { icon: Building2, title: 'Companies ready to automate operations' }
];

const results = [
  'Faster response times',
  'Higher lead conversion rates',
  'Reduced operational workload',
  'Improved customer experience',
  'Scalable systems that grow with you'
];

const AISolutions = () => {
  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[85vh] flex flex-col justify-center overflow-hidden bg-[#f8f7f4] pt-32 pb-16">
          {/* Gradient blob background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute top-0 right-0 w-[80%] h-[120%] opacity-50"
              style={{
                background: `
                  radial-gradient(ellipse 70% 50% at 75% 20%, rgba(139, 92, 246, 0.3) 0%, transparent 50%),
                  radial-gradient(ellipse 60% 50% at 85% 50%, rgba(59, 130, 246, 0.25) 0%, transparent 50%),
                  radial-gradient(ellipse 50% 60% at 65% 70%, rgba(236, 72, 153, 0.2) 0%, transparent 50%)
                `,
                filter: 'blur(60px)',
                transform: 'rotate(-10deg)'
              }}
            />
          </div>

          <div className="relative z-10 px-6 md:px-12 lg:px-24">
            {/* Back link */}
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-gray-600 hover:text-black transition-colors mb-12"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>

            <div className="max-w-4xl">
              <span className="text-sm uppercase tracking-widest text-gray-500 mb-4 block">AI Solutions</span>
              <h1 className="text-[clamp(2.5rem,5vw,4.5rem)] font-light leading-[1.1] text-black mb-6">
                AI Solutions That Work{' '}
                <span className="font-medium">for Your Business</span>
              </h1>
              <p className="text-xl text-gray-700 max-w-2xl mb-4">
                Custom AI Built to Automate, Scale, and Drive Growth
              </p>
              <p className="text-lg text-gray-600 max-w-2xl mb-8">
                We design and deploy custom AI solutions that handle conversations, tasks, and workflows—so your business runs smarter and faster. 
                From AI chatbots and voice AI to autonomous agents and intelligent automations, we build AI systems tailored to your exact business needs.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-colors"
                >
                  Book a Free AI Strategy Call <ArrowRight size={18} />
                </a>
                <a
                  href="#solutions"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-black text-black rounded-full font-medium hover:bg-black hover:text-white transition-colors"
                >
                  See AI Use Cases
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Why AI Now */}
        <section className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-medium mb-6">
                  Why Businesses Are Adopting <span className="text-violet-400">AI Now</span>
                </h2>
                <p className="text-gray-400 text-lg mb-8">
                  AI isn't about replacing people—it's about removing friction.
                </p>
                <ul className="space-y-4 mb-8">
                  {whyAI.map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check size={18} className="text-violet-400 flex-shrink-0" />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-lg text-white font-medium">
                  If your business relies on manual processes, AI can change everything.
                </p>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-violet-500/20 to-blue-500/20 rounded-3xl p-8">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-800 rounded-xl p-6 flex flex-col items-center text-center">
                      <Bot className="w-10 h-10 text-violet-400 mb-3" />
                      <span className="text-white font-medium">AI Chatbots</span>
                    </div>
                    <div className="bg-gray-800 rounded-xl p-6 flex flex-col items-center text-center">
                      <Phone className="w-10 h-10 text-blue-400 mb-3" />
                      <span className="text-white font-medium">Voice AI</span>
                    </div>
                    <div className="bg-gray-800 rounded-xl p-6 flex flex-col items-center text-center">
                      <Cpu className="w-10 h-10 text-pink-400 mb-3" />
                      <span className="text-white font-medium">AI Agents</span>
                    </div>
                    <div className="bg-gray-800 rounded-xl p-6 flex flex-col items-center text-center">
                      <Workflow className="w-10 h-10 text-cyan-400 mb-3" />
                      <span className="text-white font-medium">Automations</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* AI Solutions */}
        <section id="solutions" className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="mb-16">
              <h2 className="text-[clamp(3rem,8vw,8rem)] font-light text-gray-300 leading-none tracking-tight">
                <span className="block">OUR AI</span>
                <span className="block ml-[10%]">SOLUTIONS</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {aiSolutions.map((solution, index) => (
                <div key={index} className="bg-gray-50 rounded-3xl p-8 md:p-10 hover:shadow-lg transition-shadow">
                  <solution.icon className="w-12 h-12 text-violet-600 mb-6" />
                  <h3 className="text-2xl font-medium text-black mb-4">{solution.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{solution.description}</p>
                  <div>
                    <span className="text-sm font-medium text-gray-500 uppercase tracking-wider">Use Cases</span>
                    <ul className="mt-3 space-y-2">
                      {solution.useCases.map((useCase, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-gray-700">
                          <Check size={16} className="text-violet-500 flex-shrink-0" />
                          {useCase}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What Makes Our AI Different */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-sm uppercase tracking-widest text-violet-600 mb-4 block">Our Difference</span>
                <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                  Built for Real <span className="text-violet-600">Business Use</span>
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  We don't sell off-the-shelf AI tools. We build custom AI systems designed around your workflows, data, and goals.
                </p>
                <div className="mb-8">
                  <p className="text-gray-700 font-medium mb-4">Our approach:</p>
                  <ul className="space-y-4">
                    {ourApproach.map((item, index) => (
                      <li key={index} className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                          <item.icon size={20} className="text-violet-600" />
                        </div>
                        <span className="text-gray-700">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-lg text-black font-medium">
                  If it doesn't save time, make money, or improve performance—we don't build it.
                </p>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-violet-100 via-blue-50 to-pink-100 rounded-3xl p-8">
                  <div className="bg-white rounded-2xl shadow-xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-full bg-violet-600 flex items-center justify-center">
                        <Bot className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-black">AI Assistant</p>
                        <p className="text-xs text-gray-500">Online • Trained on your business</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-gray-100 rounded-xl p-3 max-w-[80%]">
                        <p className="text-sm text-gray-700">Hi! How can I help you today?</p>
                      </div>
                      <div className="bg-violet-600 rounded-xl p-3 max-w-[80%] ml-auto">
                        <p className="text-sm text-white">I'd like to schedule a consultation</p>
                      </div>
                      <div className="bg-gray-100 rounded-xl p-3 max-w-[80%]">
                        <p className="text-sm text-gray-700">I can help with that! Let me check our availability...</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium mb-6">
                Our AI Implementation Process
              </h2>
            </div>

            <div className="grid md:grid-cols-5 gap-8">
              {process.map((item, index) => (
                <div key={index} className="relative">
                  <span className="text-6xl font-light text-gray-700">{item.step}</span>
                  <h3 className="text-lg font-medium text-white mt-2 mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.description}</p>
                  {index < process.length - 1 && (
                    <div className="hidden md:block absolute top-8 right-0 w-full h-px bg-gray-700 -mr-4"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who Is This For */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Who Our AI Solutions Are For
              </h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-12">
              {whoIsFor.map((item, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl p-6 text-center hover:shadow-md transition-shadow">
                  <item.icon className="w-10 h-10 text-violet-600 mx-auto mb-4" />
                  <h3 className="text-sm font-medium text-black">{item.title}</h3>
                </div>
              ))}
            </div>

            <p className="text-center text-lg text-gray-700 font-medium">
              If your team is stretched thin, AI can help.
            </p>
          </div>
        </section>

        {/* Results */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-12">
                Results You Can Expect
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {results.map((result, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6 shadow-sm flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                      <Check size={20} className="text-violet-600" />
                    </div>
                    <p className="text-gray-800 font-medium text-left">{result}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="contact" className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-light mb-6">
                Ready to Build AI That{' '}
                <span className="font-medium">Works for You?</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Let's identify where AI can save time, increase revenue, and streamline your operations.
              </p>
              <a
                href="mailto:hello@gomobyle.com"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-black rounded-full font-medium hover:bg-gray-200 transition-colors mb-4"
              >
                Book a Free AI Strategy Call <ArrowRight size={18} />
              </a>
              <p className="text-gray-500 text-sm">
                No hype. No guesswork. Just custom AI solutions.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <CookieBanner />
    </div>
  );
};

export default AISolutions;
